<template>
  <main ref="horsePage" class="main" :class="{ 'no-background': useBackground }">
    <Loading v-if="isLoading" />
    <div v-else-if="initError" class="app-init-error">
      <div class="app-init-error__card">
        <i
          class="bi bi-exclamation-triangle-fill app-init-error__icon"
          aria-hidden="true"
        ></i>
        <h3 class="app-init-error__message">{{ initError }}</h3>
      </div>
    </div>
    <template v-else>
      <template v-if="$route.meta.layout === 'blank'">
        <router-view />
      </template>
      <template v-else>
        <SisLayout v-if="theme == 'sis'" />
        <DefaultLayout v-else />
      </template>
    </template>
    <modal />
    <alert />
  </main>
</template>
<script>
import languageMixins from "./mixins/language/language-mixins";
import websocketComposer from "@/composables/useWebSocket";
import "bootstrap-icons/font/bootstrap-icons.css";
import { getParamQueryTheme, isStrEmpty } from "@/utils/utils";
import indexService from "@/services/index.service";

import { setDataLanguage } from "./services/axios";
import modal from "./components/common/modal.vue";
import Loading from "@/components/common/loading.vue";
import EventBus from "./utils/EventBus";
import SisLayout from "@/layouts/sis/index.vue";
import DefaultLayout from "@/layouts/default/index.vue";
import alert from "@/components/common/alert.vue";
import calculate from "@/mixins/calculate/calculate";
import iframeHeightComposer from "@/composables/useIframeHeight";
import { notifyParentBetslipTheme } from "@/utils/betslipParentTheme";

export default {
  mixins: [languageMixins, websocketComposer, calculate, iframeHeightComposer],
  components: { SisLayout, DefaultLayout, modal, Loading, alert },
  computed: {},
  data() {
    return {
      isLoading: true,
      init: true,
      theme: "default",
      useBackground: true,
      initError: "",
    };
  },

  beforeDestroy() {
    //EventBus.$off("update-height")
    window.removeEventListener("message", this.handleParentMessage);
  },

  created() {
    this.fetchDataInit();

    // check is use background
    this.useBackground =
      !isStrEmpty(getParamQueryTheme("use_background")) &&
      getParamQueryTheme("use_background") === "false";

    // check is  mobile
    this.checkShowBetslipButton();
    window.addEventListener("resize", this.checkShowBetslipButton);
    window.addEventListener("message", this.handleParentMessage);
  },

  methods: {
    async fetchDataInit() {
      const account = getParamQueryTheme("account");
      const token = getParamQueryTheme("token");
      const currencyCode = getParamQueryTheme("currency");
      const lang = getParamQueryTheme("lang") ?? "en";

      if (isStrEmpty(account)) {
        this.initError = this.getTranslatedText("account is required");
        this.isLoading = false;
        return;
      }

      if (isStrEmpty(token) && isStrEmpty(account)) {
        localStorage.clear();
        return;
      }

      const filterGameParam = getParamQueryTheme("filter-game");
      const filterGames = !isStrEmpty(filterGameParam)
        ? filterGameParam.split(",").map((g) => g.trim().toLowerCase())
        : null;

      let game = getParamQueryTheme("game");
      if (isStrEmpty(game)) {
        const games = await this.fetchAccountGames(account);
        if (!games?.length) {
          this.initError = this.getTranslatedText("No games available");
          this.isLoading = false;
          return;
        }
        let availableGames = games;
        if (filterGames?.length) {
          availableGames = games.filter((g) =>
            filterGames.includes(g.slug.toLowerCase()),
          );
        }
        if (availableGames.length === 0) {
          this.initError = this.getTranslatedText("No games available");
          this.isLoading = false;
          return;
        }
        game = availableGames[0].slug;
      } else if (
        filterGames?.length &&
        !filterGames.includes(game.toLowerCase())
      ) {
        this.initError = this.getTranslatedText("game is not allowed");
        this.isLoading = false;
        return;
      }
      if (game) {
        // update game in query
        const query = Object.fromEntries(new URLSearchParams(window.location.search));
        this.$router.replace({ query: { ...query, game } });
        // fetch data
        await this.fetchSettings(account, game, currencyCode);
        await this.fetchDataLanguage(account, game, lang);
      }
      else {
        this.initError = this.getTranslatedText("game is required");
        this.isLoading = false;
      }
    },

    async fetchAccountGames(account) {
      try {
        const resp = await indexService.getGamesByAccount(account);
        return resp.data;
      } catch (error) {
        this.initError =
          error.message || this.getTranslatedText("failed to load games");
        return;
      }
    },

    async fetchSettings(account, game, currencyCode) {
      try {
        const settings = await indexService.getSettings(account, game);

        if (settings?.status === "success") {
          const { account_currency, account_settings, account_games } =
            settings.data;
          const setting =
            account_currency.find((x) => x.currency_code === currencyCode) ||
            account_currency[0];

          const skin = !isStrEmpty(account_settings.skin)
            ? account_settings.skin
            : process.env.VUE_APP_BASE_SKIN;

          await import(`@/public/scss/variable/${skin}/_variable.scss`);

          if (setting) {
            const payloadSettings = {
              betMin: +setting.bet_min,
              betMax: +setting.bet_max,
              betDefault: +setting.bet_default,
              taxWinnings: +setting.tax_winnings,
              timeZone: account_settings.timezone,
              multiple_bet_minimum_total_odds:
                setting.multiple_bet_minimum_total_odds,
              single_bet_minimum_odds: setting.single_bet_minimum_odds,
              maxPayout: setting.payout_max,
              showLiveStream: account_settings.show_streaming,
              account_games,
              currentTab: this.$store.state.settings.currentTab ?? "SINGLE",
              currencySymbol: setting.currency_symbol,
              currentGame: game,
              wagerTax: setting.wager_tax,
              wagerTaxType: setting.wager_tax_type,
              theme: !isStrEmpty(account_settings.theme)
                ? account_settings.theme
                : process.env.VUE_APP_THEME,
            };

            this.theme = payloadSettings.theme;
            this.$store.dispatch("updateSettings", payloadSettings);

            this.$nextTick(() => {
              notifyParentBetslipTheme();
            });

            // set default amount for multiple bet
            const amount = this.handleCalculateDefaultAmount(
              payloadSettings.betMin,
              payloadSettings.betDefault,
            );
            this.$store.commit("UPDATE_TOTAL_STAKE_MULTIPLE", amount);
          }
        }
      } catch (error) {
        this.initError =
          error.message || this.getTranslatedText("failed to load settings");
      } finally {
        this.isLoading = false;
      }
    },

    async fetchDataLanguage(account, game, lang) {
      try {
        const resp = await indexService.getLocale(account, game, lang);

        if (resp.status === "success") {
          setDataLanguage(JSON.stringify(resp.data.message));
          this.$emit("refreshNavbar");
          EventBus.$emit("refresh");
        }
      } catch (error) {
        console.log(error);
      }
    },

    checkShowBetslipButton() {
      this.$store.state.isMobileScreen = window.innerWidth <= 768;
    },

    handleParentMessage(event) {
      const { type, command, data } = event.data;

      if (type === "toggle-betslip") {
        EventBus.$emit("toggle-betslip", data.isOpen);
      }

      if (command === "refresh_data") {
        this.handleRefreshData();
      }
    },

    async handleRefreshData() {
      const query = Object.fromEntries(new URLSearchParams(window.location.search));

      await this.fetchDataInit();

      if (this.$route.name !== "next-up") {
        await this.$router.push({ path: "/", query });
        await this.$nextTick();
      }

      EventBus.$emit("get-nextup-racing");
    },
  },
};
</script>

<style lang="scss">
@import "@/public/scss/main.scss";

main {
  background: var(--background-main) var(--background-image, none) no-repeat top
    right;
  min-height: -moz-available; /* Firefox */
  min-height: -webkit-fill-available; /* Chrome/Safari */

  &.no-background {
    background: transparent;
  }
}

.app-init-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 32px 16px;

  &__card {
    width: 100%;
    max-width: 420px;
    padding: 32px 28px;
    border: 1px solid #f5c2c7;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 18px 40px rgba(220, 53, 69, 0.12);
    text-align: center;
  }

  &__icon {
    display: inline-block;
    margin-bottom: 16px;
    color: #dc3545;
    font-size: 48px;
    line-height: 1;
  }

  &__title {
    margin: 0 0 12px;
    color: #212529;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;
  }

  &__message {
    margin: 0 0 24px;
    color: #495057;
    font-size: 15px;
    line-height: 1.6;
    word-break: break-word;
  }
}
</style>
